import React, { memo, Fragment, useRef, useEffect, useState } from "react";

//react-bootstrap
import { Row, Col, Button, Form } from "react-bootstrap";

//components
import Card from "../../../components/bootstrap/card";

//Hooks
import useDataTable from "../../../components/hooks/useDatatable";

import $ from "jquery";
import exportToExcel from "../../../utilities/exportToExcel";
import useSalesAnalysisList from "../../../hooks/sales/useSalesAnalysisList";

import useUserMe from "../../../hooks/useUserMe";

const SalesAnalysis = () => {
  const { data: userMeData, isLoading } = useUserMe();
  const storeId = userMeData?.instructor?.storeId;

  // 연도 선택 상태
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // 실제 API 연동: storeId, selectedYear 전달
  const { salesAnalysisData, loading, error } = useSalesAnalysisList(storeId, selectedYear);

  // 최소 2초 로딩 유지
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    let timer;
    if (loading) {
      setShowLoading(true);
      timer = setTimeout(() => {
        if (!loading) setShowLoading(false);
      }, 2000);
    } else {
      // loading이 false가 된 후 2초가 지났으면 바로 끄고, 아니면 2초 후 끔
      timer = setTimeout(() => setShowLoading(false), 2000);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const columns = useRef([
    {
      data: null,
      title: "번호",
      render: function (data, type, row, meta) {
        return meta.row + 1;
      },
    },
    { data: "storeName", title: "지점명" },
    { data: "classificationCount.INDIVIDUAL", title: "개인" },
    { data: "classificationCount.GROUP", title: "그룹" },
    { data: "classificationCount.MIX", title: "믹스" },
    { data: "classificationCount.LEADER", title: "지도자" },
    { data: "classificationCount.OUTSIDE", title: "외부" },
    { data: "classificationCount.ETC", title: "기타" },
  ]);

  const listtableRef = useRef(null);

  useEffect(() => {
    if (listtableRef.current && salesAnalysisData) {
      if ($.fn.DataTable.isDataTable("#datatable-ecom2")) {
        $("#datatable-ecom2").DataTable().destroy();
      }
      $("#datatable-ecom2").DataTable({
        data: salesAnalysisData,
        columns: columns.current,
        createdRow: function (row, data, dataIndex) {
          $(row).find("td:eq(1), td:eq(3)").css("text-align", "center");
        },
      });
    }
  }, [salesAnalysisData]);

  useDataTable({
    tableRef: listtableRef,
    columns: columns.current,
    data: salesAnalysisData,
  });

  // 월 이름 매핑
  const monthNames = [
    "1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"
  ];

  // 실제 API 데이터 변환
  const monthlyData = (salesAnalysisData || []).map((item) => ({
    month: monthNames[(item.month || 1) - 1],
    totalAmount: item.totalSalesAmount || 0,
    individualCount: item.individualLessonCount || 0,
    groupCount: item.groupLessonCount || 0,
  }));

  // 연간 합계 계산
  const yearTotal = monthlyData.reduce(
    (acc, cur) => {
      acc.totalAmount += cur.totalAmount;
      acc.individualCount += cur.individualCount;
      acc.groupCount += cur.groupCount;
      return acc;
    },
    { totalAmount: 0, individualCount: 0, groupCount: 0 }
  );

  // 스켈레톤 스타일 추가
  const skeletonRowCount = 12;
  const skeletonStyle = {
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
    backgroundSize: '400% 100%',
    animation: 'skeleton-loading 1.4s ease infinite',
    borderRadius: 4,
    minHeight: 24,
    width: '100%',
    display: 'inline-block',
  };

  return (
    <Fragment>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="header-title">
                <h4 className="card-title">매입/매출 통계</h4>
                <p className="text-muted mb-0">
                  우측에서 년도를 선택하실 수 있습니다.
                </p>
                <p className="text-secondary mb-0">
                  선택한 년도: {selectedYear}년
                </p>
              </div>
              <div>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  style={{ width: 120, display: "inline-block" }}
                >
                  {Array.from({ length: Math.max(currentYear, 2026) - 2024 + 1 }, (_, i) => {
                    const year = Math.max(currentYear, 2026) - i;
                    return (
                      <option key={year} value={year}>
                        {year}년
                      </option>
                    );
                  })}
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body className="px-5">
              <div className="table-responsive">
                <table className="table table-bordered custom-table text-center align-middle mb-0">
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 140 }} />
                  </colgroup>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 80 }}>월</th>
                      <th>총 매출금액</th>
                      <th>개인레슨 합계</th>
                      <th>그룹레슨 합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showLoading ? (
                      Array.from({ length: skeletonRowCount }).map((_, idx) => (
                        <tr key={idx} style={{ opacity: showLoading ? 1 : 0, transition: 'opacity 0.3s' }}>
                          <td><span style={skeletonStyle}>&nbsp;</span></td>
                          <td><span style={skeletonStyle}>&nbsp;</span></td>
                          <td><span style={skeletonStyle}>&nbsp;</span></td>
                          <td><span style={skeletonStyle}>&nbsp;</span></td>
                        </tr>
                      ))
                    ) : (
                      <>
                        {monthlyData.map((row, idx) => (
                          <tr key={idx} style={{ opacity: showLoading ? 0 : 1, transition: 'opacity 0.3s' }}>
                            <td>{row.month}</td>
                            <td>{row.totalAmount.toLocaleString()} 원</td>
                            <td><span className="text-secondary">{row.individualCount}</span></td>
                            <td><span className="text-secondary">{row.groupCount}</span></td>
                          </tr>
                        ))}
                        {/* 연간 합계 행 */}
                        <tr style={{ fontWeight: 'bold', background: '#f8f9fa' }}>
                          <td>합계</td>
                          <td>{yearTotal.totalAmount.toLocaleString()} 원</td>
                          <td><span className="text-secondary">{yearTotal.individualCount}</span></td>
                          <td><span className="text-secondary">{yearTotal.groupCount}</span></td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <style>{`
@keyframes skeleton-loading {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
`}</style>
    </Fragment>
  );
};

export default SalesAnalysis;
